import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useGetBontonQrMappingRecordListMutation } from "../../../features/supplyBeam/supplyBeamData/bontonListingAPI";

const initialRequest = {
    limit: 10,
    offset: 0,
    parent_qr_code: "",
    child_qr_code: "",
    start_date: "",
    end_date: "",
};

const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
};

const BontonQrMappingList = ({ status, title }) => {
    const [
        fetchMappedQrList,
        {
            data: mappedQrListData,
            error: mappedQrListError,
            isLoading: mappedQrListIsLoading,
        },
    ] = useGetBontonQrMappingRecordListMutation();

    const [request, setRequest] = useState(initialRequest);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);

    const mappedRows = mappedQrListData?.data?.list || [];
    const totalRows = mappedQrListData?.data?.pagination?.total || 0;

    const fetchMappedQrListFunction = (nextRequest = request) => {
        fetchMappedQrList({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            data: {
                ...nextRequest,
                status,
            },
        });
    };

    useEffect(() => {
        fetchMappedQrListFunction();
    }, [request, status]);

    useEffect(() => {
        if (mappedQrListError) {
            toast.dismiss();
            toast.error("Something went wrong", { id: `${title}Error` });
        }
    }, [mappedQrListError, title]);

    const handlePageChange = (pageNumber) => {
        const nextOffset = (pageNumber - 1) * request.limit;
        setRequest((prev) => ({ ...prev, offset: nextOffset }));
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage) => {
        setRequest((prev) => ({
            ...prev,
            limit: newPerPage,
            offset: 0,
        }));
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
    };

    const clearFilters = () => {
        setRequest(initialRequest);
        setCurrentPage(1);
        setPaginationResetToggle((prev) => !prev);
    };

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => request.offset + Number(idx + 1),
            width: "80px",
        },
        {
            name: "Parent QR ID",
            cell: (row) => row.parent_qr_id || "-",
            sortable: true,
            width: "140px",
        },
        {
            name: "Parent Batch Running Code",
            cell: (row) => row.parent_batch_running_code || "-",
            sortable: true,
            width: "220px",
        },
        {
            name: "Child QR ID",
            cell: (row) => row.child_qr_id || "-",
            sortable: true,
            width: "130px",
        },
        {
            name: "Child Batch Running Code",
            cell: (row) => row.child_batch_running_code || "-",
            sortable: true,
            width: "220px",
        },
        {
            name: "Size",
            cell: (row) => row.size || "-",
            sortable: true,
            width: "120px",
        },
        {
            name: "Color",
            cell: (row) => row.color || "-",
            sortable: true,
            width: "140px",
        },
        {
            name: "Qty",
            cell: (row) => row.qty ?? "-",
            sortable: true,
            width: "100px",
        },
        {
            name: "Product Name",
            cell: (row) => row.product_name || "-",
            sortable: true,
            width: "220px",
        },
        {
            name: "Product Category",
            cell: (row) => row.product_category || "-",
            sortable: true,
            width: "180px",
        },
        {
            name: "Created Date",
            cell: (row) => formatDateTime(row.created_date),
            sortable: true,
            width: "200px",
        },
    ];

    return (
        <div className="flex h-full w-full justify-center bg-gray-200">
            <div className="w-full">
                <div className="border-b border-gray-300 bg-white py-2 pl-6 text-xl font-bold shadow-sm">
                    {title}
                    <LinksComponent />
                </div>

                <div className="p-3">
                    <div className="w-full rounded-lg bg-white shadow">
                        <DataTableComponent
                            keyField={(row) => row.mapping_id}
                            columns={columns}
                            data={mappedRows}
                            loading={mappedQrListIsLoading}
                            totalRows={totalRows}
                            handlePerRowsChange={handlePerRowsChange}
                            handlePageChange={handlePageChange}
                            paginationDefaultPage={currentPage}
                            paginationResetDefaultPage={paginationResetToggle}
                        >
                            <div className="border-b border-gray-200 bg-gray-50 p-4">
                                <div className="grid grid-cols-2 items-end gap-3 md:grid-cols-3 lg:grid-cols-5">
                                    <div className="flex flex-col">
                                        <label className="mb-1 text-xs font-semibold text-gray-600">Parent QR Code</label>
                                        <input
                                            type="text"
                                            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                            placeholder="Search Parent QR..."
                                            value={request.parent_qr_code}
                                            onChange={(e) => {
                                                setRequest({ ...request, parent_qr_code: e.target.value, offset: 0 });
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="mb-1 text-xs font-semibold text-gray-600">Child QR Code</label>
                                        <input
                                            type="text"
                                            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                            placeholder="Search Child QR..."
                                            value={request.child_qr_code}
                                            onChange={(e) => {
                                                setRequest({ ...request, child_qr_code: e.target.value, offset: 0 });
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="mb-1 text-xs font-semibold text-gray-600">From Date</label>
                                        <input
                                            type="date"
                                            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                            value={request.start_date}
                                            onChange={(e) => {
                                                setRequest({ ...request, start_date: e.target.value, offset: 0 });
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="mb-1 text-xs font-semibold text-gray-600">To Date</label>
                                        <input
                                            type="date"
                                            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-blue-500"
                                            value={request.end_date}
                                            onChange={(e) => {
                                                setRequest({ ...request, end_date: e.target.value, offset: 0 });
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <button
                                            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                                            onClick={() => fetchMappedQrListFunction()}
                                        >
                                            Search
                                        </button>
                                        <button
                                            className="rounded-md bg-gray-200 px-4 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-300"
                                            onClick={clearFilters}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </DataTableComponent>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BontonQrMappingList;
